import { fetchUserData } from '@/api/Admin/User';
import { updateUserRole } from '@/api/Admin/role';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

interface UserInfo {
  id: string;
  email: string;
  name: string;
  role: string;
}

interface FetchedUserRole {
  name: string;
}

interface FetchedUserData {
  id: number | string;
  email: string;
  fullname?: string;
  full_name_display?: string;
  assigned_roles?: FetchedUserRole[];
  roles?: FetchedUserRole[];
}

interface RoleModalProps {
  isOpen: boolean;
  onClose: () => void;
  user: {
    id: string | number;
  };
}

const roleOptions = ['admin', 'user', 'market_director'] as const;

interface UpdateUserRoleResponse {
  status: boolean;
  message?: string;
}

export const RoleModal: React.FC<RoleModalProps> = ({ isOpen, onClose, user }) => {
  const { t } = useTranslation();
  const [role, setRole] = useState('user');
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const originalOverflow = document.body.style.overflow;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = originalOverflow;
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    let isCancelled = false;

    const loadUser = async () => {
      if (!user.id) {
        setUserInfo(null);
        setRole('user');
        return;
      }

      setIsLoading(true);
      try {
        const response = await fetchUserData(String(user.id)) as FetchedUserData;

        if (isCancelled) {
          return;
        }

        const nextRole = response.assigned_roles?.[0]?.name || response.roles?.[0]?.name || 'user';

        setUserInfo({
          id: String(response.id),
          email: response.email,
          name: response.fullname || response.full_name_display || '',
          role: nextRole,
        });
        setRole(nextRole);
      } catch (error) {
        if (!isCancelled) {
          setUserInfo(null);
          setRole('user');
          toast.error(error instanceof Error ? error.message : t('roleModal.loadError'));
        }
      } finally {
        if (!isCancelled) {
          setIsLoading(false);
        }
      }
    };

    void loadUser();

    return () => {
      isCancelled = true;
    };
  }, [user, isOpen]);

  const handleClose = () => {
    setRole('user');
    setUserInfo(null);
    onClose();
  };

  const handleAssignRole = async () => {
    if (!userInfo) return;

    setIsLoading(true);
    try {
      const response = await updateUserRole(userInfo.id, role) as UpdateUserRoleResponse;

      if (!response.status) {
        throw new Error(response.message || t('roleModal.assignError'));
      }

      toast.success(response.message || t('roleModal.assignSuccess', { role }));
      handleClose();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t('roleModal.assignError'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    isOpen ? (
      <div
        className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 px-4"
        onClick={handleClose}
      >
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="assign-role-title"
          className="w-full max-w-lg rounded-2xl bg-white shadow-2xl"
          onClick={(event) => event.stopPropagation()}
        >
          <div className="flex items-start justify-between border-b border-slate-200 px-6 py-4">
            <div>
              <h2 id="assign-role-title" className="text-lg font-semibold text-slate-900">
                {t('roleModal.title')}
              </h2>
              <p className="mt-1 text-sm text-slate-500">
                {t('roleModal.subtitle')}
              </p>
            </div>
            <button
              type="button"
              onClick={handleClose}
              className="rounded-lg p-2 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600"
              aria-label={t('roleModal.closeAriaLabel')}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                className="h-5 w-5"
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 6l12 12M18 6L6 18" />
              </svg>
            </button>
          </div>

          <div className="space-y-4 px-6 py-5">
            {userInfo && (
              <div className="space-y-4">
                <div className="space-y-2">
                  <label htmlFor="user-name" className="block text-sm font-medium text-slate-700">
                    {t('roleModal.userName')}
                  </label>
                  <input
                    id="user-name"
                    type="text"
                    value={userInfo.name}
                    readOnly
                    className="w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-700 outline-none"
                  />
                </div>

                <div className="space-y-2">
                  <label htmlFor="user-email" className="block text-sm font-medium text-slate-700">
                    {t('roleModal.email')}
                  </label>
                  <input
                    id="user-email"
                    type="text"
                    value={userInfo.email}
                    readOnly
                    className="w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-700 outline-none"
                  />
                </div>

                <div className="space-y-2">
                  <label htmlFor="role" className="block text-sm font-medium text-slate-700">
                    {t('roleModal.assignRole')}
                  </label>
                  <select
                    id="role"
                    value={role}
                    onChange={(event) => setRole(event.target.value)}
                    className="w-full rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm text-slate-900 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                  >
                    {roleOptions.map((roleOption) => (
                      <option key={roleOption} value={roleOption}>
                        {roleOption}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}
          </div>

          <div className="flex flex-col-reverse gap-3 border-t border-slate-200 px-6 py-4 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={handleClose}
              className="inline-flex items-center justify-center rounded-xl border border-slate-300 px-4 py-2.5 text-sm font-medium text-slate-700 transition hover:bg-slate-50"
            >
              {t('roleModal.cancel')}
            </button>
            <button
              type="button"
              onClick={handleAssignRole}
              disabled={!userInfo || isLoading}
              className="inline-flex items-center justify-center rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-medium text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-blue-300"
            >
              {isLoading && userInfo ? (
                <span className="h-5 w-5 animate-spin rounded-full border-2 border-white/40 border-t-white" />
              ) : (
                t('roleModal.assignButton')
              )}
            </button>
          </div>
        </div>
      </div>
    ) : null
  );
};
